import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ChatStatus, isChatTextContent } from "@/shared/domain/chat/ChatRecord";
import { BROWSER_CLIENT_TOOLS } from "@/shared/domain/chat/clientTools";
import { useCancelGeneration } from "@/shared/domain/chat/hooks/useCancelGeneration";
import { useChat } from "@/shared/domain/chat/hooks/useChat";
import { useClientToolExecution } from "@/shared/domain/chat/hooks/useClientToolExecution";
import { useClientToolPreferences } from "@/shared/domain/chat/hooks/useClientToolPreferences";
import { useSendMessage } from "@/shared/domain/chat/hooks/useSendMessage";
import { useStartChat } from "@/shared/domain/chat/hooks/useStartChat";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useAIChat } from "./AIChatContext";
import styles from "./Chat.module.css";
import { ChatByIdView } from "./ChatByIdView";
import { ChatInputBox } from "./ChatInputBox";
import { ClientToolSettingsModal } from "./ClientToolSettingsModal";
import { NewChatView } from "./NewChatView";

/**
 * Chat component orchestrates all data fetching and mutations.
 * It conditionally renders NewChat vs ChatMessages based on whether chatId exists.
 * ChatInput is rendered as a pure form component with callbacks.
 */
export const Chat: React.FC = () => {
  const orgId = useCurrentOrgId();
  const {
    currentChatId,
    setCurrentChatId,
    context,
    modelProfile,
    autoEnabled,
  } = useAIChat();

  // Data fetching
  const chatQuery = useChat(currentChatId, {
    orgId,
    loadMessages: true,
  });
  const chat = chatQuery.data;

  // Client-side tool preferences (read-only — mutation is in the settings modal)
  const { data: preferences } = useClientToolPreferences();

  // Client-side tool execution with approval gating
  const { pendingApproval, handleApprove, handleDecline } =
    useClientToolExecution(chat, orgId, autoEnabled, preferences);

  // Settings modal state (opened from confirmation modal gear icon)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);

  // Mutations
  const startChatMutation = useStartChat();
  const sendMessageMutation = useSendMessage();
  const cancelGenerationMutation = useCancelGeneration();

  // Cancellation pending state - cleared when backend confirms cancellation
  const [isCancelling, setIsCancelling] = React.useState(false);
  const isSubmittingRef = React.useRef(false);

  // Clear cancellation state when chat status changes to USER_TURN
  React.useEffect(() => {
    if (chat?.status === ChatStatus.USER_TURN) {
      setIsCancelling(false);
    }
  }, [chat?.status]);

  // Derived state: the chat is "loading" when it exists and isn't the user's
  // turn, OR when the chat query is in its initial loading state (bridges the
  // gap between startChat completing and the query resolving).
  const loading =
    chatQuery.isLoading ||
    (chatQuery.data !== undefined &&
      chatQuery.data?.status !== ChatStatus.USER_TURN);

  // Mutation handlers
  const handleStartChat = (message: string) => {
    // Use mutate + onSuccess instead of mutateAsync so React batches
    // the isPending→false and setCurrentChatId state updates in the
    // same render, preventing a flash of the send button.
    return new Promise<void>((resolve, reject) => {
      startChatMutation.mutate(
        {
          message,
          orgId,
          context,
          modelProfile,
          clientTools: BROWSER_CLIENT_TOOLS,
        },
        {
          onSuccess: (chatRecord) => {
            setCurrentChatId(chatRecord.session_id);
            resolve();
          },
          onError: (error) => reject(error),
        },
      );
    });
  };

  const handleSendMessage = async (message: string) => {
    if (!currentChatId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      chatId: currentChatId,
      message: message,
      orgId,
      context,
      clientTools: BROWSER_CLIENT_TOOLS,
    });
  };

  // Callbacks for ChatInput
  const handleSubmit = async (message: string): Promise<void> => {
    const shouldSendMessage =
      currentChatId !== undefined && !sendMessageMutation.isPending;
    const shouldStartChat =
      currentChatId === undefined &&
      message.trim() !== "" &&
      !startChatMutation.isPending;

    if (isSubmittingRef.current || (!shouldSendMessage && !shouldStartChat)) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      if (shouldSendMessage) {
        await handleSendMessage(message);
      } else {
        await handleStartChat(message);
      }
    } catch (err) {
      ErrorMonitoringService.captureError(err);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (!startChatMutation.isPending) {
      await handleStartChat(question);
    }
  };

  const handleCancel = async () => {
    // Reset mutations to cancel any pending requests
    startChatMutation.reset();
    sendMessageMutation.reset();

    // If we have an active chat that's generating, request cancellation from the backend
    if (currentChatId && chat?.status !== ChatStatus.USER_TURN) {
      setIsCancelling(true);
      try {
        await cancelGenerationMutation.mutateAsync({ chatId: currentChatId });
      } catch (err) {
        setIsCancelling(false);
        ErrorMonitoringService.captureError(err);
      }
    }
  };

  const handleRetry = React.useCallback(
    (messageIndex: number) => {
      if (!chat) {
        return;
      }

      const message = chat.messages[messageIndex];
      const textContent = message?.content.find(isChatTextContent);

      if (textContent === undefined) {
        return;
      }

      sendMessageMutation.reset();
      void sendMessageMutation.retry(chat.session_id, textContent.text);
    },
    [chat, sendMessageMutation],
  );

  const sending = startChatMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {startChatMutation.isError && (
          <Alert
            severity="error"
            onClose={() => {
              startChatMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startChatMutation.error?.message || "An error occurred"}
          </Alert>
        )}
        {/* Conditional Views */}
        <div className={styles.chatView}>
          {chat ? (
            <ChatByIdView
              chat={chat}
              isCancelling={isCancelling}
              onRetry={handleRetry}
              pendingApproval={pendingApproval}
              onApprove={() => void handleApprove()}
              onDecline={handleDecline}
            />
          ) : (
            <NewChatView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <ChatInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
          isCancelling={isCancelling}
          chatModelProfile={chat?.model_profile}
        />
      </div>

      {/* Settings modal (can be opened from approval card or AutoToggle gear) */}
      <ClientToolSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </>
  );
};
